import React, { useState, useMemo, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Box,
  Calculator,
  Info,
  BookOpen,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  Copy,
  Check,
  RotateCcw,
  Circle,
  Cone,
  Cylinder,
  Pyramid,
  Ruler,
  Scale3d,
} from "lucide-react";

const PI = Math.PI;

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// 3D Shape SVG Visualizations
const SphereVisualization = ({ radius }) => {
  const r = Math.min(70, Math.max(20, radius * 10));
  return (
    <svg viewBox="0 0 200 200" className="w-full h-full max-w-[220px] mx-auto">
      <defs>
        <radialGradient id="sphereGradient" cx="30%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#34d399" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#059669" stopOpacity="0.9" />
        </radialGradient>
        <filter id="sphereShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.3" />
        </filter>
      </defs>
      {/* Grid */}
      <line x1="0" y1="100" x2="200" y2="100" stroke="hsl(var(--border))" strokeWidth="1" strokeDasharray="4 4" />
      <line x1="100" y1="0" x2="100" y2="200" stroke="hsl(var(--border))" strokeWidth="1" strokeDasharray="4 4" />
      {/* Sphere */}
      <circle cx="100" cy="100" r={r} fill="url(#sphereGradient)" filter="url(#sphereShadow)" />
      {/* Equator line */}
      <ellipse cx="100" cy="100" rx={r} ry={r * 0.3} fill="none" stroke="#065f46" strokeWidth="1.5" strokeDasharray="3 3" />
      {/* Radius line */}
      <line x1="100" y1="100" x2={100 + r} y2="100" stroke="#dc2626" strokeWidth="2" />
      <circle cx={100 + r} cy="100" r="4" fill="#dc2626" />
      <circle cx="100" cy="100" r="3" fill="#1f2937" />
      {/* Label */}
      <text x={100 + r/2} y="90" fontSize="12" fill="#dc2626" fontWeight="bold" textAnchor="middle">r</text>
    </svg>
  );
};

const ConeVisualization = ({ radius, height }) => {
  const r = Math.min(60, Math.max(20, radius * 8));
  const h = Math.min(120, Math.max(40, height * 8));
  const topY = 100 - h/2;
  const bottomY = 100 + h/2;
  
  return (
    <svg viewBox="0 0 200 200" className="w-full h-full max-w-[220px] mx-auto">
      <defs>
        <linearGradient id="coneGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#d97706" stopOpacity="0.9" />
        </linearGradient>
        <filter id="coneShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.3" />
        </filter>
      </defs>
      {/* Cone body */}
      <polygon points={`100,${topY} ${100-r},${bottomY} ${100+r},${bottomY}`} fill="url(#coneGradient)" filter="url(#coneShadow)" />
      {/* Base ellipse */}
      <ellipse cx="100" cy={bottomY} rx={r} ry={r * 0.3} fill="#b45309" />
      {/* Height line */}
      <line x1="100" y1={topY} x2="100" y2={bottomY} stroke="#3b82f6" strokeWidth="2" strokeDasharray="4 4" />
      {/* Radius line */}
      <line x1="100" y1={bottomY} x2={100 + r} y2={bottomY} stroke="#dc2626" strokeWidth="2" />
      <circle cx={100 + r} cy={bottomY} r="4" fill="#dc2626" />
      <circle cx="100" cy={topY} r="4" fill="#f59e0b" />
      {/* Labels */}
      <text x="110" y={(topY + bottomY)/2} fontSize="12" fill="#3b82f6" fontWeight="bold">h</text>
      <text x={100 + r/2} y={bottomY + 20} fontSize="12" fill="#dc2626" fontWeight="bold" textAnchor="middle">r</text>
    </svg>
  );
};

const CylinderVisualization = ({ radius, height }) => {
  const r = Math.min(50, Math.max(20, radius * 8));
  const h = Math.min(100, Math.max(40, height * 8));
  const topY = 100 - h/2;
  const bottomY = 100 + h/2;
  const ellipseRy = r * 0.3;
  
  return (
    <svg viewBox="0 0 200 200" className="w-full h-full max-w-[220px] mx-auto">
      <defs>
        <linearGradient id="cylinderGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.8" />
          <stop offset="50%" stopColor="#22d3ee" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.8" />
        </linearGradient>
        <filter id="cylinderShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.3" />
        </filter>
      </defs>
      {/* Cylinder body */}
      <rect x={100 - r} y={topY} width={r * 2} height={h} fill="url(#cylinderGradient)" filter="url(#cylinderShadow)" />
      {/* Bottom ellipse */}
      <ellipse cx="100" cy={bottomY} rx={r} ry={ellipseRy} fill="#0891b2" />
      {/* Top ellipse */}
      <ellipse cx="100" cy={topY} rx={r} ry={ellipseRy} fill="#22d3ee" />
      {/* Height line */}
      <line x1={100 + r + 15} y1={topY} x2={100 + r + 15} y2={bottomY} stroke="#3b82f6" strokeWidth="2" />
      <line x1={100 + r + 10} y1={topY} x2={100 + r + 20} y2={topY} stroke="#3b82f6" strokeWidth="2" />
      <line x1={100 + r + 10} y1={bottomY} x2={100 + r + 20} y2={bottomY} stroke="#3b82f6" strokeWidth="2" />
      {/* Radius line */}
      <line x1="100" y1={topY} x2={100 + r} y2={topY} stroke="#dc2626" strokeWidth="2" />
      <circle cx={100 + r} cy={topY} r="4" fill="#dc2626" />
      <circle cx="100" cy={topY} r="3" fill="#1f2937" />
      {/* Labels */}
      <text x={100 + r + 25} y={(topY + bottomY)/2 + 4} fontSize="12" fill="#3b82f6" fontWeight="bold">h</text>
      <text x={100 + r/2} y={topY - 10} fontSize="12" fill="#dc2626" fontWeight="bold" textAnchor="middle">r</text>
    </svg>
  );
};

const PyramidVisualization = ({ baseLength, height }) => {
  const b = Math.min(80, Math.max(30, baseLength * 6));
  const h = Math.min(110, Math.max(40, height * 8));
  const topY = 100 - h/2;
  const bottomY = 100 + h/2;
  const halfBase = b / 2;
  const depth = b * 0.4;
  
  return (
    <svg viewBox="0 0 200 200" className="w-full h-full max-w-[220px] mx-auto">
      <defs>
        <linearGradient id="pyramidGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a855f7" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.9" />
        </linearGradient>
        <linearGradient id="pyramidSide" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#9333ea" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#6b21a8" stopOpacity="0.7" />
        </linearGradient>
        <filter id="pyramidShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.3" />
        </filter>
      </defs>
      {/* Left face */}
      <polygon points={`100,${topY} ${100 - halfBase},${bottomY} 100,${bottomY - depth}`} fill="url(#pyramidSide)" filter="url(#pyramidShadow)" />
      {/* Front face */}
      <polygon points={`100,${topY} ${100 - halfBase},${bottomY} ${100 + halfBase},${bottomY}`} fill="url(#pyramidGradient)" />
      {/* Right face */}
      <polygon points={`100,${topY} ${100 + halfBase},${bottomY} 100,${bottomY - depth}`} fill="url(#pyramidSide)" opacity="0.8" />
      {/* Base edges */}
      <line x1={100 - halfBase} y1={bottomY} x2="100" y2={bottomY - depth} stroke="#581c87" strokeWidth="1.5" />
      <line x1={100 + halfBase} y1={bottomY} x2="100" y2={bottomY - depth} stroke="#581c87" strokeWidth="1.5" />
      {/* Height line */}
      <line x1="100" y1={topY} x2="100" y2={bottomY} stroke="#3b82f6" strokeWidth="2" strokeDasharray="4 4" />
      {/* Base length line */}
      <line x1={100 - halfBase} y1={bottomY + 15} x2={100 + halfBase} y2={bottomY + 15} stroke="#dc2626" strokeWidth="2" />
      <line x1={100 - halfBase} y1={bottomY + 10} x2={100 - halfBase} y2={bottomY + 20} stroke="#dc2626" strokeWidth="2" />
      <line x1={100 + halfBase} y1={bottomY + 10} x2={100 + halfBase} y2={bottomY + 20} stroke="#dc2626" strokeWidth="2" />
      <circle cx="100" cy={topY} r="4" fill="#a855f7" />
      {/* Labels */}
      <text x="110" y={(topY + bottomY)/2} fontSize="12" fill="#3b82f6" fontWeight="bold">h</text>
      <text x="100" y={bottomY + 32} fontSize="12" fill="#dc2626" fontWeight="bold" textAnchor="middle">a</text>
    </svg>
  );
};

// Shape data and formulas
const SHAPES = {
  sphere: {
    name: 'Sphere',
    icon: Circle,
    color: 'emerald',
    inputs: [{ key: 'radius', label: 'Radius (r)', default: 5 }],
    formulas: {
      volume: 'V = (4/3)πr³',
      surfaceArea: 'SA = 4πr²',
    },
    calculate: (inputs) => {
      const r = inputs.radius;
      return {
        volume: (4/3) * PI * Math.pow(r, 3),
        surfaceArea: 4 * PI * Math.pow(r, 2),
      };
    },
  },
  cone: {
    name: 'Cone',
    icon: Cone,
    color: 'amber',
    inputs: [
      { key: 'radius', label: 'Base Radius (r)', default: 4 },
      { key: 'height', label: 'Height (h)', default: 6 },
    ],
    formulas: {
      volume: 'V = (1/3)πr²h',
      surfaceArea: 'SA = πr(r + l) where l = √(r² + h²)',
    },
    calculate: (inputs) => {
      const r = inputs.radius;
      const h = inputs.height;
      const slantHeight = Math.sqrt(r * r + h * h);
      return {
        volume: (1/3) * PI * r * r * h,
        surfaceArea: PI * r * (r + slantHeight),
        slantHeight: slantHeight,
      };
    },
  },
  cylinder: {
    name: 'Cylinder',
    icon: Cylinder,
    color: 'cyan',
    inputs: [
      { key: 'radius', label: 'Radius (r)', default: 4 },
      { key: 'height', label: 'Height (h)', default: 8 },
    ],
    formulas: {
      volume: 'V = πr²h',
      surfaceArea: 'SA = 2πrh + 2πr² = 2πr(h + r)',
    },
    calculate: (inputs) => {
      const r = inputs.radius;
      const h = inputs.height;
      return {
        volume: PI * r * r * h,
        surfaceArea: 2 * PI * r * (h + r),
        lateralArea: 2 * PI * r * h,
        baseArea: PI * r * r,
      };
    },
  },
  pyramid: {
    name: 'Square Pyramid',
    icon: Pyramid,
    color: 'violet',
    inputs: [
      { key: 'baseLength', label: 'Base Length (a)', default: 6 },
      { key: 'height', label: 'Height (h)', default: 8 },
    ],
    formulas: {
      volume: 'V = (1/3)a²h',
      surfaceArea: 'SA = a² + 2a × √((a/2)² + h²)',
    },
    calculate: (inputs) => {
      const a = inputs.baseLength;
      const h = inputs.height;
      const slantHeight = Math.sqrt(Math.pow(a/2, 2) + Math.pow(h, 2));
      return {
        volume: (1/3) * a * a * h,
        surfaceArea: a * a + 2 * a * slantHeight,
        baseArea: a * a,
        slantHeight: slantHeight,
      };
    },
  },
};

function Shape3DCalculator() {
  const [activeShape, setActiveShape] = useState('sphere');
  const [inputs, setInputs] = useState({
    sphere: { radius: 5 },
    cone: { radius: 4, height: 6 },
    cylinder: { radius: 4, height: 8 },
    pyramid: { baseLength: 6, height: 8 },
  });
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);

  const shape = SHAPES[activeShape];
  const currentInputs = inputs[activeShape];
  const results = useMemo(() => shape.calculate(currentInputs), [shape, currentInputs]);

  const handleInputChange = useCallback((key, value) => {
    const numValue = parseFloat(value) || 0;
    setInputs(prev => ({
      ...prev,
      [activeShape]: {
        ...prev[activeShape],
        [key]: Math.max(0.1, numValue),
      },
    }));
  }, [activeShape]);

  const handleReset = useCallback(() => {
    const defaults = {};
    shape.inputs.forEach(inp => {
      defaults[inp.key] = inp.default;
    });
    setInputs(prev => ({
      ...prev,
      [activeShape]: defaults,
    }));
  }, [activeShape, shape.inputs]);

  const formatValue = (val) => {
    if (val === undefined || !isFinite(val)) return 'N/A';
    return val.toFixed(4);
  };

  const renderVisualization = () => {
    switch(activeShape) {
      case 'sphere': return <SphereVisualization radius={currentInputs.radius} />;
      case 'cone': return <ConeVisualization radius={currentInputs.radius} height={currentInputs.height} />;
      case 'cylinder': return <CylinderVisualization radius={currentInputs.radius} height={currentInputs.height} />;
      case 'pyramid': return <PyramidVisualization baseLength={currentInputs.baseLength} height={currentInputs.height} />;
      default: return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
            <Box className="w-6 h-6 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">3D Shape Calculator</h1>
            <p className="text-muted-foreground">Calculate surface area & volume of 3D shapes</p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Calculator Panel */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <Card className="rounded-2xl p-6 border bg-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary" />
                <span className="font-semibold text-foreground">Calculator</span>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleReset} title="Reset">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            {/* Shape Selection */}
            <div className="mb-6">
              <Label className="text-sm text-muted-foreground mb-3 block">Select Shape</Label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(SHAPES).map(([key, shapeData]) => {
                  const Icon = shapeData.icon;
                  return (
                    <Button
                      key={key}
                      variant={activeShape === key ? 'default' : 'outline'}
                      className={`h-auto py-3 flex flex-col gap-1 ${activeShape === key ? '' : 'hover:bg-muted'}`}
                      onClick={() => setActiveShape(key)}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs">{shapeData.name}</span>
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Input Fields */}
            <div className="space-y-4">
              {shape.inputs.map(input => (
                <div key={input.key}>
                  <Label className="text-sm text-muted-foreground mb-2 block">{input.label}</Label>
                  <Input
                    type="number"
                    value={currentInputs[input.key]}
                    onChange={(e) => handleInputChange(input.key, e.target.value)}
                    className="border text-lg font-mono h-12"
                    min="0.1"
                    step="0.5"
                  />
                </div>
              ))}
            </div>

            {/* Quick Values */}
            <div className="mt-4">
              <Label className="text-xs text-muted-foreground mb-2 block">Quick Values</Label>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 5, 10, 15].map(val => (
                  <Button
                    key={val}
                    variant="outline"
                    size="sm"
                    className="text-xs rounded-lg"
                    onClick={() => handleInputChange(shape.inputs[0].key, val)}
                  >
                    {val}
                  </Button>
                ))}
              </div>
            </div>

            {/* Results */}
            <div className="pt-4 mt-4 border-t border-border space-y-3">
              <Label className="text-sm font-medium text-foreground">Results</Label>
              <div className="space-y-3">
                <div className="rounded-xl p-4 bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                      <Scale3d className="w-3 h-3" /> Volume
                    </span>
                    <CopyButton text={formatValue(results.volume)} />
                  </div>
                  <span className="font-mono text-lg font-bold text-emerald-600 dark:text-emerald-400 block">
                    {formatValue(results.volume)} units³
                  </span>
                </div>
                <div className="rounded-xl p-4 bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                      <Ruler className="w-3 h-3" /> Surface Area
                    </span>
                    <CopyButton text={formatValue(results.surfaceArea)} />
                  </div>
                  <span className="font-mono text-lg font-bold text-blue-600 dark:text-blue-400 block">
                    {formatValue(results.surfaceArea)} units²
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Center/Right Column - Visualization & Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Visualization Section */}
          <Card className="rounded-2xl p-6 border bg-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-foreground flex items-center gap-2">
                <Info className="w-4 h-4 text-primary" />
                Interactive Visualization
                <Badge variant="secondary" className="ml-2 text-xs">Live</Badge>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* 3D Shape */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">3D View</h3>
                <div className="rounded-xl p-6 h-[260px] bg-muted/30 border flex items-center justify-center">
                  {renderVisualization()}
                </div>
              </div>

              {/* Properties */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Properties</h3>
                <div className="rounded-xl p-4 bg-muted/30 border space-y-4">
                  <div>
                    <span className="text-xs text-muted-foreground">Shape</span>
                    <p className="text-lg font-semibold text-foreground">{shape.name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {shape.inputs.map(input => (
                      <div key={input.key} className="bg-background rounded-lg p-3">
                        <span className="text-xs text-muted-foreground block mb-1">{input.label}</span>
                        <span className="font-mono font-semibold text-foreground">{currentInputs[input.key]}</span>
                      </div>
                    ))}
                  </div>
                  {results.slantHeight && (
                    <div className="bg-background rounded-lg p-3">
                      <span className="text-xs text-muted-foreground block mb-1">Slant Height (l)</span>
                      <span className="font-mono font-semibold text-foreground">{formatValue(results.slantHeight)}</span>
                    </div>
                  )}
                  {results.lateralArea && (
                    <div className="bg-background rounded-lg p-3">
                      <span className="text-xs text-muted-foreground block mb-1">Lateral Area</span>
                      <span className="font-mono font-semibold text-foreground">{formatValue(results.lateralArea)} units²</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Formulas */}
            <div className="mt-6 p-4 bg-muted/30 rounded-xl">
              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-primary" />
                Formulas for {shape.name}
              </h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="bg-background rounded-lg p-3">
                  <span className="text-xs text-muted-foreground block mb-1">Volume</span>
                  <code className="font-mono text-sm text-emerald-600 dark:text-emerald-400">{shape.formulas.volume}</code>
                </div>
                <div className="bg-background rounded-lg p-3">
                  <span className="text-xs text-muted-foreground block mb-1">Surface Area</span>
                  <code className="font-mono text-sm text-blue-600 dark:text-blue-400">{shape.formulas.surfaceArea}</code>
                </div>
              </div>
            </div>
          </Card>

          {/* Step-by-Step Solution */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
              <BookOpen className="w-4 h-4 text-primary" />
              Step-by-Step Solution
            </h2>

            <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
              <AccordionItem value="step-1" className="border-b border-border">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">1</span>
                    <span className="font-medium">Identify the given values</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pl-11 space-y-3">
                    <p className="text-muted-foreground">For the <span className="font-semibold text-foreground">{shape.name}</span>, we have:</p>
                    <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                      {shape.inputs.map(input => (
                        <div key={input.key} className="flex items-center gap-2">
                          <span className="text-muted-foreground">{input.label}:</span>
                          <span className="font-mono font-semibold text-foreground bg-secondary px-2 py-0.5 rounded">{currentInputs[input.key]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="step-2" className="border-b border-border">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">2</span>
                    <span className="font-medium">Apply the volume formula</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pl-11 space-y-4">
                    <div className="bg-secondary/50 rounded-lg p-4">
                      <p className="text-sm font-medium text-foreground mb-2">Volume Formula:</p>
                      <code className="font-mono text-emerald-600 dark:text-emerald-400 block text-lg">{shape.formulas.volume}</code>
                    </div>
                    <div className="bg-emerald-500/10 rounded-lg p-3 text-lg border border-emerald-500/20">
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Volume = </span>
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold">{formatValue(results.volume)} units³</span>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="step-3" className="border-0">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">3</span>
                    <span className="font-medium">Apply the surface area formula</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pl-11 space-y-4">
                    <div className="bg-secondary/50 rounded-lg p-4">
                      <p className="text-sm font-medium text-foreground mb-2">Surface Area Formula:</p>
                      <code className="font-mono text-blue-600 dark:text-blue-400 block text-lg">{shape.formulas.surfaceArea}</code>
                    </div>
                    <div className="bg-blue-500/10 rounded-lg p-3 text-lg border border-blue-500/20">
                      <span className="text-blue-600 dark:text-blue-400 font-semibold">Surface Area = </span>
                      <span className="text-blue-600 dark:text-blue-400 font-bold">{formatValue(results.surfaceArea)} units²</span>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>

          {/* Understanding 3D Shapes */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-primary" />
              Understanding 3D Shapes
            </h2>
            
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <h3 className="font-bold text-lg text-emerald-600 dark:text-emerald-400 mb-2">Sphere</h3>
                <p className="text-muted-foreground mb-3">
                  A sphere is perfectly round in 3D space - like a ball. Every point on its surface is equally distant from the center.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Volume:</strong> V = (4/3)πr³ - How much space it fills</li>
                  <li>• <strong>Surface Area:</strong> SA = 4πr² - Total area of the outer surface</li>
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <h3 className="font-bold text-lg text-amber-600 dark:text-amber-400 mb-2">Cone</h3>
                <p className="text-muted-foreground mb-3">
                  A cone has a circular base that tapers to a point (apex). Think of an ice cream cone or traffic cone.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Slant Height:</strong> l = √(r² + h²) - Distance from base edge to apex along surface</li>
                  <li>• <strong>Volume:</strong> V = (1/3)πr²h - One-third of cylinder with same base and height</li>
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                <h3 className="font-bold text-lg text-cyan-600 dark:text-cyan-400 mb-2">Cylinder</h3>
                <p className="text-muted-foreground mb-3">
                  A cylinder has two parallel circular bases connected by a curved surface. Like a can or tube.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Volume:</strong> V = πr²h - Base area times height</li>
                  <li>• <strong>Surface Area:</strong> SA = 2πr(h + r) - Two bases plus curved surface</li>
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
                <h3 className="font-bold text-lg text-violet-600 dark:text-violet-400 mb-2">Square Pyramid</h3>
                <p className="text-muted-foreground mb-3">
                  A pyramid has a square base and four triangular faces that meet at an apex. Like the Egyptian pyramids.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Slant Height:</strong> l = √((a/2)² + h²) - Height of triangular face</li>
                  <li>• <strong>Volume:</strong> V = (1/3)a²h - One-third of prism with same base and height</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Common Mistakes */}
          <Card className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Common Mistakes to Avoid
            </h2>
            <ul className="space-y-3">
              {[
                'Confusing radius with diameter - always check if you need to divide by 2',
                'Forgetting to square or cube the radius where needed',
                'Using the wrong formula for surface area (lateral vs total)',
                'Not including units in your final answer (units² for area, units³ for volume)',
                'Mixing up height (perpendicular) with slant height (along the surface)',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Real World Applications */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Real-World Applications</h2>
            <ul className="space-y-3">
              {[
                'Architecture: Calculating material needs for domes, silos, and tanks',
                'Manufacturing: Determining container volumes for packaging and shipping',
                'Engineering: Designing pipes, tunnels, and pressure vessels',
                'Food Industry: Calculating ice cream cone volumes or storage tank capacities',
                'Construction: Estimating concrete needed for pillars and foundations',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <ArrowRight className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* FAQ */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {[
                {
                  q: 'What is the difference between surface area and volume?',
                  a: 'Surface area measures the total area of all faces/surfaces of a 3D shape (in square units). Volume measures the space inside the shape (in cubic units). Think of surface area as wrapping paper needed and volume as water it can hold.'
                },
                {
                  q: 'Why is a cone\'s volume one-third of a cylinder\'s?',
                  a: 'If you have a cone and cylinder with the same base and height, exactly 3 cones of sand will fill the cylinder. This was proven mathematically by Archimedes using calculus-like methods.'
                },
                {
                  q: 'What is slant height and when do I use it?',
                  a: 'Slant height is the distance from the base edge to the apex along the surface of a cone or pyramid. It\'s used for calculating surface area, while perpendicular height is used for volume.'
                },
                {
                  q: 'How do I find the radius if I only know the diameter?',
                  a: 'The radius is half the diameter: r = d/2. For example, if a sphere has diameter 10, its radius is 5.'
                },
              ].map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-medium text-foreground">{faq.q}</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Shape3DCalculator;
